import store from 'store'
import { isInIframe } from 'utilities/domOperation'
import {
  getRejectedClientSession,
  canRememberRejectedClientSession
} from 'utilities/clientSessionCookie'

// postMessage contract agreed with CB (FE-71 / FE-73):
//   BW -> parent: { action: 'getClientSession', version: 1 }
//   parent -> BW: { action: 'getClientSession', version: 1, client_session: '<token>' | null }
//   BW -> parent: { action: 'updateClientSession', version: 1, client_session: '<token>' }
//                 when BW had to get a new session from IDP itself
export const CLIENT_SESSION_ACTION = 'getClientSession'
export const UPDATE_CLIENT_SESSION_ACTION = 'updateClientSession'
export const CLIENT_SESSION_MESSAGE_VERSION = 1

// The parent origin we send to and accept replies from. A client session is a
// bearer credential, so without a concrete origin ('*' or missing) we don't ask.
function getParentOrigin() {
  const parentUrl =
    store.getters['setting/getSetting']?.parentUrl ||
    new URLSearchParams(window.location.search).get('parentUrl')
  if (!parentUrl || parentUrl === '*') return null
  try {
    return new URL(decodeURIComponent(parentUrl)).origin
  } catch {
    return null
  }
}

function getParentWindow() {
  // Same targets as sendDataToParentWindow / sendDataToParentWindowOnRecommenderWidget
  const isRecommenderWidget = location.pathname.includes('/recommender-widget')
  return store.getters['setting/isInChatbotIframe'] || isRecommenderWidget
    ? window.top
    : window.parent
}

function postToParent(data, parentOrigin) {
  try {
    const parent = getParentWindow()
    if (!parent) return
    parent.postMessage({ ...data, version: CLIENT_SESSION_MESSAGE_VERSION }, parentOrigin)
  } catch (e) {
    console.error('postToParent failed')
    console.error(e)
  }
}

// Hand a session BW just got from IDP back to the embedding page, so it can
// replace the one it holds (e.g. the one the server refused) instead of giving
// the next product a dead one.
export function pushClientSessionToParent(session) {
  const parentOrigin = getParentOrigin()
  if (!session || !isInIframe() || !parentOrigin) return
  postToParent({ action: UPDATE_CLIENT_SESSION_ACTION, client_session: session }, parentOrigin)
}

// Ask the embedding page for its IDP client session so BW doesn't request a new
// one. Resolves with the session string, or undefined when BW isn't in an
// iframe, the parent has none (replies null), the parent hands back the session
// the server already refused in this tab, or nothing answers before timeout.
// Also skipped when storage is blocked, since the refused session could not be
// remembered across the reload and the parent could loop the page with it.
export function requestParentClientSession(timeout = 500) {
  const parentOrigin = getParentOrigin()
  if (!isInIframe() || !parentOrigin || !canRememberRejectedClientSession()) {
    return Promise.resolve()
  }
  const parentWindow = getParentWindow()

  return new Promise((resolve) => {
    // finish references the timers/handler declared below; it is only ever
    // invoked after they are initialised, so the const bindings are safe.
    const finish = (session) => {
      clearTimeout(timeoutId)
      clearInterval(intervalId)
      window.removeEventListener('message', messageHandler)
      resolve(session || undefined)
    }

    const messageHandler = (event) => {
      // must come from the window we asked, not just any frame on that origin
      if (event.source !== parentWindow || event.origin !== parentOrigin) return
      const { action, version, client_session: session } = event.data || {}
      if (action !== CLIENT_SESSION_ACTION || version !== CLIENT_SESSION_MESSAGE_VERSION) return
      // the parent may still hold the session we reloaded over; fall back to IDP
      finish(session === getRejectedClientSession() ? undefined : session)
    }

    const send = () => postToParent({ action: CLIENT_SESSION_ACTION }, parentOrigin)

    const timeoutId = setTimeout(() => finish(), timeout)
    window.addEventListener('message', messageHandler)

    // the parent may not have loaded yet, so keep asking every 200ms
    send()
    const intervalId = setInterval(send, 200)
  })
}
